/**
 * Página Explorar - Pantalla dedicada a explorar plantillas/guías
 * Mismo diseño que el Dashboard (Topbar + Sidebar + contenido)
 * Ruta: /explorar
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout, ExploreSection } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../components/PurchaseChoiceModal";
import ComingSoonModal from "../components/ComingSoonModal";
import EmailVerificationModal from "../components/EmailVerificationModal";
import { trackGuideInteraction, trackPurchaseAction } from "../utils/analytics";
import SeoHead from "../components/Seo/SeoHead";

import "../components/Dashboard/Dashboard.css";

import { GUIDE_CATALOG_IMAGES, sortGuideCatalog } from "../data/guideCatalogImages";
import plantillaTodo from "../assets/to-do.png";
import travelkitCover from "../assets/travel-kit2.png";

const imageMap = {
	...GUIDE_CATALOG_IMAGES,
	"todo-viaje": plantillaTodo,
	travelkit: travelkitCover,
};

const ExplorePage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		subscription,
		handleLogout: contextLogout,
		importPrebuiltGuide,
		getPrebuiltGuides,
		checkGuideAccess,
		purchaseGuide,
		purchaseSubscription,
		canCreateGuide,
		favorites,
		toggleFavorite,
		isBlockedByEmailVerification,
	} = useFirebase();

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [featuredGuides, setFeaturedGuides] = useState([]);
	const [loadingGuides, setLoadingGuides] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [purchasingGuide, setPurchasingGuide] = useState(null);
	const [purchaseChoiceModal, setPurchaseChoiceModal] = useState({
		open: false,
		guide: null,
		limitInfo: null,
	});
	const [comingSoonModal, setComingSoonModal] = useState({
		open: false,
		guideName: null,
	});
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);

	// Mostrar login si viene redirigido
	useEffect(() => {
		if (location.state?.showLogin) {
			if (location.state?.authMode) {
				setAuthMode(location.state.authMode);
			}
			setShowLogin(true);
			// Limpiar el estado
			window.history.replaceState({}, document.title);
		}
	}, [location]);

	useEffect(() => {
		const loadGuides = async () => {
			setLoadingGuides(true);
			try {
				const prebuiltGuides = await getPrebuiltGuides();
				const guidesWithImages = (prebuiltGuides || []).map((guide) => {
					// Verificar acceso del usuario a esta guía
					const access = checkGuideAccess(guide.id, guide);
					return {
						...guide,
						coverImage: imageMap[guide.id] || guide.coverImage,
						days: guide.days || null,
						tags: guide.tags || [],
						// Info de acceso y precio del backend
						isFree: guide.isFree,
						price: guide.price,
						userHasAccess: guide.userHasAccess ?? access.hasFullAccess,
						accessReason: guide.accessReason ?? access.reason,
					};
				});
				setFeaturedGuides(sortGuideCatalog(guidesWithImages));
			} catch (error) {
				console.error("Error loading guides:", error);
				setFeaturedGuides([
					{
						id: "tokio",
						name: "6 días en Tokio",
						coverImage: imageMap.tokio,
						days: 6,
						description: "Explora Tokio",
						tags: ["Asia", "Japón"],
						isFree: true,
					},
					{
						id: "kioto",
						name: "4 días en Kioto",
						coverImage: imageMap.kioto,
						days: 4,
						description: "Descubre Kioto",
						tags: ["Asia", "Japón"],
						price: 4.99,
					},
					{
						id: "ny",
						name: "7 días en Nueva York",
						coverImage: imageMap.ny,
						days: 7,
						description: "Nueva York",
						tags: ["América", "USA"],
						price: 9.99,
					},
					{
						id: "londres",
						name: "4 días en Londres",
						coverImage: imageMap.londres,
						days: 4,
						description: "Londres",
						tags: ["Europa", "UK"],
						price: 9.99,
					},
				]);
			} finally {
				setLoadingGuides(false);
			}
		};
		loadGuides();
		// Solo recargar cuando cambie usuario o suscripción; no cuando cambien favoritos
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, subscription]);

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") return; // ya estamos aquí
		if (sectionId === "rutas") {
			navigate("/rutas");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "create") {
			navigate("/crear-guia");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus favoritos", {
					variant: "info",
				});
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		// logros-viajeros comentado
		// if (sectionId === "logros-viajeros") {
		// 	if (!user) {
		// 		setAuthMode("login");
		// 		setShowLogin(true);
		// 		enqueueSnackbar("Inicia sesión para ver tus logros viajeros", {
		// 			variant: "info",
		// 		});
		// 		return;
		// 	}
		// 	navigate("/logros-viajeros");
		// 	return;
		// }
		if (sectionId === "recent") {
			navigate("/");
			return;
		}
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus módulos", { variant: "info" });
				return;
			}
			navigate("/modulos");
			return;
		}
		if (sectionId === "discounts") {
			navigate("/descuentos");
			return;
		}
	};

	const handleViewGuide = (guideId) => {
		trackGuideInteraction("preview_clicked", guideId, { source: "explorar" });
		navigate(`/preview/prebuilt/${guideId}`, {
			state: { from: `${location.pathname}${location.search}` },
		});
	};

	const handleCustomizeGuide = async (guideId) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			enqueueSnackbar("Inicia sesión para personalizar una guía", {
				variant: "info",
			});
			return;
		}
		trackGuideInteraction("customize_clicked", guideId, { source: "explorar" });

		const guide = featuredGuides.find((g) => g.id === guideId);

		// Verificar acceso a la guía
		if (guide && !guide.isFree && !guide.userHasAccess) {
			enqueueSnackbar(
				`Esta guía requiere compra o suscripción (${
					guide.price ? `€${guide.price}` : "Premium"
				})`,
				{ variant: "warning" },
			);
			return;
		}

		// Verificar límite de guías
		const createCheck = canCreateGuide();
		if (!createCheck.allowed) {
			setPurchaseChoiceModal({
				open: true,
				guide,
				limitInfo: {
					currentGuides: createCheck.currentGuides,
					maxGuides: createCheck.maxGuides,
				},
			});
			return;
		}

		try {
			enqueueSnackbar("Importando guía...", { variant: "info" });
			const result = await importPrebuiltGuide(guideId);
			if (result?.project) {
				enqueueSnackbar("Guía importada correctamente", { variant: "success" });
				const userId = result.project.userId || user.uid || user.id;
				navigate(`/usuarios/${userId}/guias/${result.project.id}`);
			} else {
				throw new Error("No se pudo crear la guía");
			}
		} catch (error) {
			console.error("Error importing guide:", error);
			if (
				error.code === "GUIDE_LIMIT" ||
				error.message?.includes("límite de guías")
			) {
				setPurchaseChoiceModal({
					open: true,
					guide,
					limitInfo: {
						currentGuides: createCheck.currentGuides,
						maxGuides: createCheck.maxGuides,
					},
				});
			} else if (
				error.code === "ACCESS_DENIED" ||
				error.message?.includes("acceso")
			) {
				enqueueSnackbar(
					"No tienes acceso a esta guía. Cómprala o suscríbete.",
					{ variant: "warning" },
				);
			} else {
				enqueueSnackbar(error.message || "No se pudo crear la guía", {
					variant: "error",
				});
			}
		}
	};

	const handlePurchaseClick = (guide) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			enqueueSnackbar("Inicia sesión para comprar esta guía", {
				variant: "info",
			});
			return;
		}
		if (isBlockedByEmailVerification("payment")) {
			setShowEmailVerifyModal(true);
			return;
		}
		trackPurchaseAction("modal_opened", {
			guide_id: guide?.id ? String(guide.id) : undefined,
			guide_name: guide?.name,
			source: "explorar",
		});
		setPurchaseChoiceModal({ open: true, guide });
	};

	const handleConfirmBuyGuide = async (guide) => {
		if (!guide?.id) return;
		try {
			await purchaseGuide(guide.id);
		} catch (error) {
			console.error("Error purchasing guide:", error);
			enqueueSnackbar(error.message || "Error al procesar la compra", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleConfirmSubscribe = async () => {
		try {
			await purchaseSubscription();
		} catch (error) {
			console.error("Error subscribing:", error);
			enqueueSnackbar(error.message || "Error al iniciar la suscripción", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		}
	};

	const handleCreateFromScratch = () => {
		navigate("/crear-guia");
	};

	const handleLogout = async () => {
		await contextLogout();
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	const filteredGuides = searchQuery
		? featuredGuides.filter(
				(g) =>
					g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
					g.description?.toLowerCase().includes(searchQuery.toLowerCase()),
			)
		: featuredGuides;

	return (
		<>
			<SeoHead
				title="Guías de viaje"
				description="Descubre plantillas de guías por ciudad y país: Japón, Corea del Sur, Europa y más. Personalízalas en minutos con Viajes Molones."
				path="/explorar"
				noIndex
			/>
			<DashboardLayout
				user={formattedUser}
				activeSection="explore"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={handleLogout}
				onCreateNew={handleCreateFromScratch}
				onSearch={(q) => setSearchQuery(q || "")}
				prebuiltGuides={featuredGuides}
				myGuides={[]}
				imageMap={imageMap}
				onComingSoonGuide={(g) =>
					setComingSoonModal({ open: true, guideName: g?.name ?? null })
				}
			>
				<ExploreSection
					guides={filteredGuides}
					loading={loadingGuides}
					onViewGuide={handleViewGuide}
					onCustomizeGuide={handleCustomizeGuide}
					onPurchaseGuide={handlePurchaseClick}
					onComingSoonClick={(g) =>
						setComingSoonModal({ open: true, guideName: g?.name ?? null })
					}
					purchasingGuideId={purchasingGuide}
					onSeeAll={() => navigate("/")}
					hideSeeAllButton
					user={formattedUser}
					onLogin={() => {
						setAuthMode("signup");
						setShowLogin(true);
					}}
					favoriteIds={favorites || []}
					onToggleFavorite={
						user
							? async (guideId) => {
									try {
										await toggleFavorite(guideId);
										enqueueSnackbar(
											favorites?.includes(guideId)
												? "Quitado de favoritos"
												: "Añadido a favoritos",
											{ variant: "success" },
										);
									} catch (error) {
										enqueueSnackbar("Error al actualizar favoritos", {
											variant: "error",
										});
									}
								}
							: null
					}
				/>
			</DashboardLayout>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>

			<PurchaseChoiceModal
				open={purchaseChoiceModal.open}
				guide={purchaseChoiceModal.guide}
				onClose={() =>
					setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null })
				}
				onBuyGuide={handleConfirmBuyGuide}
				onSubscribe={handleConfirmSubscribe}
				loading={!!purchasingGuide}
				limitInfo={purchaseChoiceModal.limitInfo}
			/>

			<ComingSoonModal
				open={comingSoonModal.open}
				onClose={() => setComingSoonModal({ open: false, guideName: null })}
				guideName={comingSoonModal.guideName}
			/>

			<EmailVerificationModal
				open={showEmailVerifyModal}
				onClose={() => setShowEmailVerifyModal(false)}
			/>
		</>
	);
};

export default ExplorePage;
